import { BlogService } from '@/services/blog';
import type { BlogQueryParams, CreateBlogPostDto, UpdateBlogPostDto } from '@/types/blog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

const blogKeys = {
  all: ['blog-posts'] as const,
  lists: () => [...blogKeys.all, 'list'] as const,
  list: (params: BlogQueryParams) => [...blogKeys.lists(), params] as const,
  details: () => [...blogKeys.all, 'detail'] as const,
  detail: (id: string) => [...blogKeys.details(), id] as const,
};

export const useBlogManagement = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<'published_at' | 'created_at' | 'title'>('published_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const queryParams = useMemo<BlogQueryParams>(
    () => ({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearchText || undefined,
      category: selectedCategory as BlogQueryParams['category'],
      status: selectedStatus as BlogQueryParams['status'],
      sortBy,
      sortOrder,
    }),
    [currentPage, pageSize, debouncedSearchText, selectedCategory, selectedStatus, sortBy, sortOrder]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchText.length >= 3 || searchText.length === 0) {
        setDebouncedSearchText(searchText);
      } else {
        setDebouncedSearchText('');
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, selectedCategory, selectedStatus]);

  const { data: blogResponse, isLoading, refetch } = useQuery({
    queryKey: blogKeys.list(queryParams),
    queryFn: () => BlogService.getBlogPosts(queryParams),
    staleTime: 30000,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateBlogPostDto) => BlogService.createBlogPost(data),
    onSuccess: () => {
      message.success('Blog post created successfully');
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create blog post');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBlogPostDto }) =>
      BlogService.updateBlogPost(id, data),
    onSuccess: () => {
      message.success('Blog post updated successfully');
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blogKeys.details() });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update blog post');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => BlogService.deleteBlogPost(id),
    onSuccess: () => {
      message.success('Blog post deleted successfully');
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to delete blog post');
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => BlogService.bulkDeleteBlogPosts(ids),
    onSuccess: (result) => {
      message.success(`Deleted ${result.deleted_count} blog post(s)`);
      setSelectedRowKeys([]);
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to delete blog posts');
    },
  });

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const handleCategoryFilter = useCallback((value: string | undefined) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  }, []);

  const handleStatusFilter = useCallback((value: string | undefined) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchText('');
    setDebouncedSearchText('');
    setSelectedCategory(undefined);
    setSelectedStatus(undefined);
    setCurrentPage(1);
  }, []);

  const handleTableChange = useCallback((pagination: any, _filters: any, sorter: any) => {
    if (pagination?.current) setCurrentPage(pagination.current);
    if (pagination?.pageSize) setPageSize(pagination.pageSize);

    if (sorter?.field) {
      const fieldMap: Record<string, 'published_at' | 'created_at' | 'title'> = {
        published_at: 'published_at',
        created_at: 'created_at',
        title: 'title',
      };
      const mappedField = fieldMap[sorter.field as string];
      if (mappedField) {
        setSortBy(mappedField);
        setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
      }
    }
  }, []);

  const handleCreateBlogPost = useCallback(
    async (data: CreateBlogPostDto) => createMutation.mutateAsync(data),
    [createMutation]
  );

  const handleUpdateBlogPost = useCallback(
    async (id: string, data: UpdateBlogPostDto) => updateMutation.mutateAsync({ id, data }),
    [updateMutation]
  );

  const handleDeleteBlogPost = useCallback(
    async (id: string) => deleteMutation.mutateAsync(id),
    [deleteMutation]
  );

  const handleBulkDelete = useCallback(
    async (ids: string[]) => bulkDeleteMutation.mutateAsync(ids),
    [bulkDeleteMutation]
  );

  return {
    posts: blogResponse?.data ?? [],
    loading: isLoading,
    total: blogResponse?.total ?? 0,
    currentPage,
    pageSize,
    searchText,
    selectedCategory,
    selectedStatus,
    sortBy,
    sortOrder,
    selectedRowKeys,
    bulkLoading: bulkDeleteMutation.isPending,
    handleSearch,
    handleCategoryFilter,
    handleStatusFilter,
    clearFilters,
    handleTableChange,
    handleCreateBlogPost,
    handleUpdateBlogPost,
    handleDeleteBlogPost,
    handleBulkDelete,
    setSelectedRowKeys,
    refetch,
  };
};

export const useBlogPost = (id?: string) =>
  useQuery({
    queryKey: blogKeys.detail(id ?? ''),
    queryFn: () => BlogService.getBlogPostById(id as string),
    enabled: Boolean(id),
  });

export default useBlogManagement;
